import { Injectable } from '@angular/core';

export interface InputDependency {
  parameterName: string;
  sourceQflowId: string;
}

export interface OutputDependency {
  targetQflowId: string;
  columnIndex: number;
}

export interface DependencyMaps {
  input: Map<string, InputDependency[]>;
  sourceToTargets: Map<string, string[]>;
  outputBySourceControl: Map<string, OutputDependency[]>;
}

export interface FlowDataWithDependencies {
  id?: string;
  dependencies?: unknown[];
}

@Injectable({ providedIn: 'root' })
export class DomainDependencyParserService {
  parseDependencies(
    formJson: any,
    flowData?: readonly FlowDataWithDependencies[] | null
  ): DependencyMaps {
    const inputMap = new Map<string, InputDependency[]>();
    const sourceToTargets = new Map<string, string[]>();
    const outputBySourceControl = new Map<string, OutputDependency[]>();

    const body = formJson?.body ?? formJson?.Body ?? [];
    this.traverseElements(body, inputMap, sourceToTargets, outputBySourceControl);

    if (flowData?.length) {
      this.mergeFromFlowData(
        inputMap,
        sourceToTargets,
        outputBySourceControl,
        flowData
      );
    }

    return { input: inputMap, sourceToTargets, outputBySourceControl };
  }

  private removeTargetFromCascadeMap(
    sourceToTargets: Map<string, string[]>,
    targetControlKey: string
  ): void {
    for (const [sourceId, targets] of sourceToTargets) {
      const filtered = targets.filter(t => t !== targetControlKey);
      if (filtered.length === 0) {
        sourceToTargets.delete(sourceId);
      } else if (filtered.length !== targets.length) {
        sourceToTargets.set(sourceId, filtered);
      }
    }
  }

  private mergeFromFlowData(
    inputMap: Map<string, InputDependency[]>,
    sourceToTargets: Map<string, string[]>,
    outputBySourceControl: Map<string, OutputDependency[]>,
    flowData: readonly FlowDataWithDependencies[]
  ): void {
    for (const fd of flowData) {
      const controlKey = fd.id;
      const rawDeps = fd.dependencies;
      if (!controlKey || !Array.isArray(rawDeps) || rawDeps.length === 0) {
        continue;
      }

      const inputDeps: InputDependency[] = [];
      const outputDeps: OutputDependency[] = [];
      for (const dep of rawDeps) {
        const inNorm = this.normalizeDependency(dep);
        if (inNorm) {
          inputDeps.push({
            parameterName: inNorm.parameterName,
            sourceQflowId: inNorm.dataId,
          });
        }
        const outNorm = this.normalizeOutputDependency(dep);
        if (outNorm) {
          outputDeps.push(outNorm);
        }
      }

      if (inputDeps.length > 0) {
        this.removeTargetFromCascadeMap(sourceToTargets, controlKey);
        inputMap.set(controlKey, inputDeps);

        for (const dep of inputDeps) {
          const sourceId = dep.sourceQflowId;
          if (!sourceToTargets.has(sourceId)) {
            sourceToTargets.set(sourceId, []);
          }
          if (!sourceToTargets.get(sourceId)!.includes(controlKey)) {
            sourceToTargets.get(sourceId)!.push(controlKey);
          }
        }
      }

      if (outputDeps.length > 0) {
        outputBySourceControl.set(controlKey, outputDeps);
      }
    }
  }

  private resolveControlKey(element: any): string | undefined {
    const k =
      element?.qflowId ??
      element?.QflowId ??
      element?.id ??
      element?.Id;
    return typeof k === 'string' && k.length > 0 ? k : undefined;
  }

  private getDependenciesArray(element: any): any[] | undefined {
    const d = element?.dependencies ?? element?.Dependencies;
    return Array.isArray(d) && d.length > 0 ? d : undefined;
  }

  private isInputParameterDirection(direction: unknown): boolean {
    if (direction === 1 || direction === '1') {
      return true;
    }
    if (typeof direction === 'string') {
      return direction.toLowerCase() === 'input';
    }
    return false;
  }

  private isOutputParameterDirection(direction: unknown): boolean {
    if (direction === 2 || direction === '2') {
      return true;
    }
    if (typeof direction === 'string') {
      return direction.toLowerCase() === 'output';
    }
    return false;
  }

  private normalizeDependency(dep: any): { parameterName: string; dataId: string } | undefined {
    if (!dep) {
      return undefined;
    }
    const parameterName = dep.parameterName ?? dep.ParameterName;
    const rawDataId = dep.dataId ?? dep.DataId;
    const dataId =
      typeof rawDataId === 'string'
        ? rawDataId
        : rawDataId != null
          ? String(rawDataId)
          : '';
    if (!this.isInputParameterDirection(dep.parameterDirection ?? dep.ParameterDirection)) {
      return undefined;
    }
    if (typeof parameterName !== 'string' || !parameterName) {
      return undefined;
    }
    if (!dataId) {
      return undefined;
    }
    return { parameterName, dataId };
  }

  private normalizeOutputDependency(dep: any): OutputDependency | undefined {
    if (!dep) {
      return undefined;
    }
    if (!this.isOutputParameterDirection(dep.parameterDirection ?? dep.ParameterDirection)) {
      return undefined;
    }
    const rawTargetId = dep.dataId ?? dep.DataId;
    const targetQflowId =
      typeof rawTargetId === 'string'
        ? rawTargetId
        : rawTargetId != null
          ? String(rawTargetId)
          : '';
    const colRaw = dep.columnIndex ?? dep.ColumnIndex ?? 0;
    const columnIndex = typeof colRaw === 'number' ? colRaw : Number(colRaw);
    if (!targetQflowId || !Number.isFinite(columnIndex)) {
      return undefined;
    }
    return { targetQflowId, columnIndex };
  }

  private traverseElements(
    elements: any[],
    inputMap: Map<string, InputDependency[]>,
    sourceToTargets: Map<string, string[]>,
    outputBySourceControl: Map<string, OutputDependency[]>
  ): void {
    if (!Array.isArray(elements)) return;

    for (const element of elements) {
      if (!element) continue;

      const controlKey = this.resolveControlKey(element);
      const deps = this.getDependenciesArray(element);

      if (controlKey && deps) {
        const inputDeps: InputDependency[] = [];
        const outputDeps: OutputDependency[] = [];

        for (const dep of deps) {
          const norm = this.normalizeDependency(dep);
          if (norm) {
            inputDeps.push({
              parameterName: norm.parameterName,
              sourceQflowId: norm.dataId,
            });

            const sourceId = norm.dataId;
            if (!sourceToTargets.has(sourceId)) {
              sourceToTargets.set(sourceId, []);
            }
            if (!sourceToTargets.get(sourceId)!.includes(controlKey)) {
              sourceToTargets.get(sourceId)!.push(controlKey);
            }
          }
          const outNorm = this.normalizeOutputDependency(dep);
          if (outNorm) {
            outputDeps.push(outNorm);
          }
        }

        if (inputDeps.length > 0) {
          inputMap.set(controlKey, inputDeps);
        }
        if (outputDeps.length > 0) {
          outputBySourceControl.set(controlKey, outputDeps);
        }
      }

      this.traverseChildren(element, inputMap, sourceToTargets, outputBySourceControl);
    }
  }

  private traverseChildren(
    element: any,
    inputMap: Map<string, InputDependency[]>,
    sourceToTargets: Map<string, string[]>,
    outputBySourceControl: Map<string, OutputDependency[]>
  ): void {
    if (Array.isArray(element.items)) {
      this.traverseElements(element.items, inputMap, sourceToTargets, outputBySourceControl);
    }

    if (Array.isArray(element.columns)) {
      for (const col of element.columns) {
        if (Array.isArray(col?.items)) {
          this.traverseElements(col.items, inputMap, sourceToTargets, outputBySourceControl);
        }
      }
    }

    if (Array.isArray(element.facts)) {
      this.traverseElements(element.facts, inputMap, sourceToTargets, outputBySourceControl);
    }
  }
}
